import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import {
  CONTRACT_REQUIRED_PATHS,
  fetchOpenApiContractStatus,
  type OpenApiContractStatus,
} from "../lib/openApiContract";
import type { components } from "../lib/api-types";

type FRIAResult = components["schemas"]["FriaDossier"];
type TenantIsolationStatus = components["schemas"]["TenantIsolationStatus"];
type TenantDenyReport = components["schemas"]["TenantDenyReport"];
type TenantPersistenceReport = components["schemas"]["TenantPersistenceReport"];
type TenantModelRoutingReport = components["schemas"]["TenantModelRoutingReport"];
type ModelRoutingDecisionRequest = components["schemas"]["ModelRoutingDecisionRequest"];
type ModelRoutingDecisionApprovalRequest = components["schemas"]["ModelRoutingDecisionApprovalRequest"];
type ModelRoutingDecision = components["schemas"]["ModelRoutingDecision"];
type AuditIntegrityReport = components["schemas"]["AuditIntegrityReport"];
type AuditChainStatus = components["schemas"]["AuditChainStatus"];
type AuditResealResult = components["schemas"]["AuditResealResult"];
type AuditRecord = components["schemas"]["AuditRecord"];
type AgentRunReplay = components["schemas"]["AgentRunReplay"];
type AgentRunEvidenceVerification = components["schemas"]["AgentRunEvidenceVerification"];

type ResealAuditItem = {
  id?: string;
  applied?: boolean;
  dry_run?: boolean;
  run_id?: string | null;
  evidence_id?: string | null;
  archive_uri?: string | null;
  manifest_uri?: string | null;
  kinds?: Array<{
    kind?: string;
    applied?: boolean;
    records?: number;
    resealable?: boolean;
    before_error?: string | null;
    error?: string | null;
  }>;
};

const ISOLATION_LABEL: Record<string, string> = {
  pass: "Pass",
  warning: "Warnung",
  fail: "Fail",
  not_configured: "Roadmap",
};

export default function CompliancePage() {
  const api = useApi();
  const qc = useQueryClient();
  const { effectiveTenantId } = useTenant();
  const [generated, setGenerated] = useState<FRIAResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<AuditResealResult | null>(null);
  const [applyResult, setApplyResult] = useState<AuditResealResult | null>(null);
  const [modelDecision, setModelDecision] = useState<ModelRoutingDecision | null>(null);
  const [friaReplayResult, setFriaReplayResult] = useState<AgentRunReplay | null>(null);

  const isolation = useQuery({
    queryKey: ["tenant-isolation-status", effectiveTenantId],
    queryFn: () => api.get<TenantIsolationStatus>("/tenant-isolation/status"),
    refetchInterval: 30_000,
  });

  const denyFixtures = useQuery({
    queryKey: ["tenant-deny-fixtures", effectiveTenantId],
    queryFn: () => api.get<TenantDenyReport>("/tenant-isolation/deny-fixtures"),
    refetchInterval: 30_000,
  });

  const persistence = useQuery({
    queryKey: ["tenant-persistence-report", effectiveTenantId],
    queryFn: () => api.get<TenantPersistenceReport>("/tenant-isolation/persistence"),
    refetchInterval: 30_000,
  });

  const modelRouting = useQuery({
    queryKey: ["tenant-model-routing", effectiveTenantId],
    queryFn: () => api.get<TenantModelRoutingReport>("/tenant-isolation/model-routing"),
    refetchInterval: 30_000,
  });

  const openApiContract = useQuery({
    queryKey: ["openapi-contract", effectiveTenantId],
    queryFn: () => fetchOpenApiContractStatus(effectiveTenantId),
    enabled: Boolean(effectiveTenantId),
    refetchInterval: 60_000,
  });

  const integrity = useQuery({
    queryKey: ["audit-integrity-report", effectiveTenantId],
    queryFn: () => api.get<AuditIntegrityReport>("/audit/integrity-report"),
    refetchInterval: 30_000,
  });

  const dossiers = useQuery({
    queryKey: ["fria-dossiers", effectiveTenantId],
    queryFn: () => api.get<FRIAResult[]>("/compliance/fria/dossiers?limit=1"),
    refetchInterval: 30_000,
  });

  const resealRecords = useQuery({
    queryKey: ["audit-reseals", effectiveTenantId],
    queryFn: () => api.get<AuditRecord[]>("/audit/records?kind=audit_reseals&limit=3"),
    refetchInterval: 30_000,
  });

  const generate = useMutation({
    mutationFn: () => api.post<FRIAResult>("/agent-runs/compliance-fria/trigger", {}),
    onSuccess: (data) => {
      setGenerated(data);
      setFriaReplayResult(null);
      qc.invalidateQueries({ queryKey: ["fria-dossiers", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-integrity-report", effectiveTenantId] });
    },
  });

  const resealDryRun = useMutation({
    mutationFn: () => api.post<AuditResealResult>("/audit/reseal", {
      actor: "operator-ui",
      reason: "operator dry-run from compliance gate",
      dry_run: true,
    }),
    onSuccess: (data) => {
      setDryRunResult(data);
      setApplyResult(null);
    },
  });

  const resealApply = useMutation({
    mutationFn: () => api.post<AuditResealResult>("/audit/reseal", {
      actor: "operator-ui",
      reason: "operator-approved audit reseal from compliance gate",
      dry_run: false,
    }),
    onSuccess: (data) => {
      setApplyResult(data);
      setDryRunResult(null);
      qc.invalidateQueries({ queryKey: ["tenant-isolation-status", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-integrity-report", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-reseals", effectiveTenantId] });
    },
  });

  const decideModelRoute = useMutation({
    mutationFn: (payload: ModelRoutingDecisionRequest) =>
      api.post<ModelRoutingDecision>("/tenant-isolation/model-routing/decide", payload),
    onSuccess: (data) => {
      setModelDecision(data);
      qc.invalidateQueries({ queryKey: ["tenant-model-routing", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["tenant-persistence-report", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["tenant-isolation-status", effectiveTenantId] });
    },
  });

  const resolveModelRoute = useMutation({
    mutationFn: ({
      id,
      action,
      payload,
    }: {
      id: string;
      action: "approve" | "reject";
      payload: ModelRoutingDecisionApprovalRequest;
    }) =>
      api.post<ModelRoutingDecision>(
        `/tenant-isolation/model-routing/decisions/${encodeURIComponent(id)}/${action}`,
        payload,
      ),
    onSuccess: (data) => {
      setModelDecision(data);
      qc.invalidateQueries({ queryKey: ["tenant-model-routing", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["tenant-persistence-report", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["tenant-isolation-status", effectiveTenantId] });
    },
  });

  const latest = generated ?? dossiers.data?.[0] ?? null;
  const latestRunId = latest?.run_id ?? "";

  const friaReplays = useQuery({
    queryKey: ["fria-agent-replays", effectiveTenantId, latestRunId],
    queryFn: () => api.get<AgentRunReplay[]>(`/agent-runs/${encodeURIComponent(latestRunId)}/replays?limit=1`),
    enabled: Boolean(latestRunId),
  });

  const friaEvidenceGate = useQuery({
    queryKey: ["fria-agent-run-evidence-verification", effectiveTenantId, latestRunId],
    queryFn: () => api.get<AgentRunEvidenceVerification>(`/agent-runs/${encodeURIComponent(latestRunId)}/evidence-verification`),
    enabled: Boolean(latestRunId),
    refetchInterval: 30_000,
  });

  const replayFria = useMutation({
    mutationFn: (runId: string) => api.post<AgentRunReplay>(`/agent-runs/${encodeURIComponent(runId)}/replay`, {}),
    onSuccess: (data) => {
      setFriaReplayResult(data);
      qc.invalidateQueries({ queryKey: ["fria-agent-replays", effectiveTenantId, data.run_id] });
      qc.invalidateQueries({ queryKey: ["fria-agent-run-evidence-verification", effectiveTenantId, data.run_id] });
      qc.invalidateQueries({ queryKey: ["audit-integrity-report", effectiveTenantId] });
    },
  });

  const latestReplay = friaReplayResult ?? friaReplays.data?.[0] ?? null;

  return (
    <div className="page">
      <h1>Compliance &amp; FRIA</h1>
      <p className="subtitle">
        EU AI Act Art. 27 — Quartalsdossier deterministisch generiert. DPO/CISO-Signatur via Documenso.
      </p>

      <section className="cards">
        <div className="card">
          <div className="card-title">Letztes Dossier</div>
          <div className="card-value">
            {latest ? `Q${latest.quarter} ${latest.year}` : <span className="muted small">noch keines erzeugt</span>}
          </div>
        </div>
        <div className={`card ${latest?.risk_label === "critical" || latest?.risk_label === "high" ? "alert" : ""}`}>
          <div className="card-title">Risk-Score</div>
          <div className="card-value">
            {latest ? `${latest.risk_score} (${latest.risk_label})` : <span className="muted small">folgt mit dem ersten Dossier</span>}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Signatur ausstehend</div>
          <div className="card-value">
            {latest ? (latest.requires_signature ? "ja" : "nein") : <span className="muted small">nichts zu signieren</span>}
          </div>
        </div>
      </section>

      <OpenApiContractGate
        contract={openApiContract.data}
        loading={openApiContract.isLoading}
        error={openApiContract.error}
      />

      <TenantIsolationGate
        status={isolation.data}
        loading={isolation.isLoading}
        error={isolation.error}
        denyReport={denyFixtures.data}
        denyLoading={denyFixtures.isLoading}
        denyError={denyFixtures.error}
        persistence={persistence.data}
        persistenceLoading={persistence.isLoading}
        persistenceError={persistence.error}
        modelRouting={modelRouting.data}
        modelRoutingLoading={modelRouting.isLoading}
        modelRoutingError={modelRouting.error}
        modelDecision={modelDecision}
        modelDecisionPending={decideModelRoute.isPending}
        modelDecisionError={decideModelRoute.error}
        onModelDecision={(payload) => decideModelRoute.mutate(payload)}
        modelApprovalPending={resolveModelRoute.isPending}
        modelApprovalError={resolveModelRoute.error}
        onModelApproval={(decision, action) => resolveModelRoute.mutate({
          id: decision.id,
          action,
          payload: {
            actor: "operator-ui",
            role: decision.required_approvals?.[0] ?? "tenant_admin",
            comment: action === "approve"
              ? "approved from compliance model-routing gate"
              : "rejected from compliance model-routing gate",
          },
        })}
      />
      <AuditResealOperator
        report={integrity.data}
        loading={integrity.isLoading}
        error={integrity.error}
        dryRunResult={dryRunResult}
        applyResult={applyResult}
        dryRunPending={resealDryRun.isPending}
        applyPending={resealApply.isPending}
        dryRunError={resealDryRun.error}
        applyError={resealApply.error}
        records={resealRecords.data ?? []}
        recordsLoading={resealRecords.isLoading}
        recordsError={resealRecords.error}
        onDryRun={() => resealDryRun.mutate()}
        onApply={() => resealApply.mutate()}
      />

      <section>
        <button
          className="btn"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
        >
          {generate.isPending ? "Generiere…" : "FRIA-Dossier generieren"}
        </button>
        {generate.error && (
          <p className="error">Fehler: {(generate.error as { message?: string }).message}</p>
        )}
      </section>

      {latest && (
        <section>
          <h2>Artifacts</h2>
          <ul className="metric-list">
            {(latest.artifacts ?? []).map((a) => (
              <li key={a} className="metric-item">
                <code>{a}</code>
                <span className="muted small"> · {latest.evidence_uri}/{a}</span>
              </li>
            ))}
          </ul>
          <p className="muted small">
            Alle Artifacts liegen tenant-scoped in MinIO. DPO und CISO erhalten den Pfad zur Documenso-Signatur.
          </p>
          <p className="muted small">
            Run <code>{latest.run_id}</code> · Evidence <code>{latest.evidence_id ?? "pending"}</code> · {latest.model_route}
          </p>
          <FriaReplayEvidencePanel
            dossier={latest}
            replay={latestReplay}
            evidenceGate={friaEvidenceGate.data}
            replayPending={replayFria.isPending}
            replayError={replayFria.error}
            evidenceLoading={friaEvidenceGate.isLoading}
            evidenceError={friaEvidenceGate.error}
            onReplay={() => replayFria.mutate(latest.run_id)}
          />
        </section>
      )}
    </div>
  );
}

function OpenApiContractGate({
  contract,
  loading,
  error,
}: {
  contract?: OpenApiContractStatus;
  loading: boolean;
  error: unknown;
}) {
  const missing = contract?.requiredPaths.filter((item) => !item.present) ?? [];
  const status = !contract
    ? "loading"
    : missing.length > 0 || !contract.apiRewrite.json || contract.apiRewrite.htmlFallback
      ? "fail"
      : "pass";

  return (
    <section className={`openapi-contract-gate ${status}`}>
      <div className="section-head compact">
        <div>
          <p className="hero-eyebrow">Public API v1 Contract</p>
          <h2>Frontend und Runtime laufen gegen denselben API-Vertrag.</h2>
          <p className="muted small">
            OpenAPI wird live vom Orchestrator gelesen; `/api/v1/*` muss JSON liefern und darf nicht in die SPA fallen.
          </p>
        </div>
        <span className={`status-pill ${status === "fail" ? "danger" : status === "pass" ? "good" : "warning"}`}>
          {loading ? "lade" : status}
        </span>
      </div>

      {Boolean(error) && <p className="error">OpenAPI-Contract konnte nicht geladen werden: {errorMessage(error)}</p>}

      <div className="openapi-contract-summary">
        <div><span>Spec</span><strong>{contract?.openapi ?? "—"}</strong></div>
        <div><span>Version</span><strong>{contract?.version ?? "—"}</strong></div>
        <div><span>Paths</span><strong>{contract?.pathCount.toLocaleString("de-DE") ?? "—"}</strong></div>
        <div><span>Schemas</span><strong>{contract?.schemaCount.toLocaleString("de-DE") ?? "—"}</strong></div>
        <div>
          <span>/api/v1 rewrite</span>
          <strong>{contract ? `${contract.apiRewrite.status} · ${contract.apiRewrite.json ? "JSON" : "nicht JSON"}` : "—"}</strong>
        </div>
      </div>

      <ul className="openapi-path-list">
        {(contract?.requiredPaths ?? CONTRACT_REQUIRED_PATHS.map((item) => ({ ...item, present: false }))).map((item) => (
          <li key={item.path} className={item.present ? "pass" : "fail"}>
            <span className={`pill ${item.present ? "good" : "warn"}`}>{item.present ? "covered" : "missing"}</span>
            <div>
              <strong>{item.label}</strong>
              <code>{item.path}</code>
            </div>
          </li>
        ))}
      </ul>

      {contract?.apiRewrite.htmlFallback && (
        <p className="error">API-Rewrite liefert HTML-SPA-Fallback. Das ist release-blocking.</p>
      )}
      {missing.length > 0 && (
        <p className="error">{missing.length} produktkritische API-Pfade fehlen im Live-Contract.</p>
      )}
    </section>
  );
}

function FriaReplayEvidencePanel({
  dossier,
  replay,
  evidenceGate,
  replayPending,
  replayError,
  evidenceLoading,
  evidenceError,
  onReplay,
}: {
  dossier: FRIAResult;
  replay: AgentRunReplay | null;
  evidenceGate?: AgentRunEvidenceVerification;
  replayPending: boolean;
  replayError: unknown;
  evidenceLoading: boolean;
  evidenceError: unknown;
  onReplay: () => void;
}) {
  return (
    <div className="fria-replay-panel">
      <div className="section-head compact">
        <div>
          <h3>FRIA Replay &amp; Evidence Gate</h3>
          <p className="muted small">
            Deterministischer Nachweis für Dossier, Audit-Lineage und tenant-spezifische Evidence.
          </p>
        </div>
        <span className={`pill ${replay?.status === "passed" || evidenceGate?.status === "pass" ? "good" : "warn"}`}>
          {replay?.status ?? evidenceGate?.status ?? "nicht replayed"}
        </span>
      </div>

      <div className="run-focus-grid">
        <div><span>Dossier</span><strong>Q{dossier.quarter} {dossier.year}</strong></div>
        <div><span>Run</span><strong>{dossier.run_id}</strong></div>
        <div><span>Risk</span><strong>{dossier.risk_score} ({dossier.risk_label})</strong></div>
        <div><span>Model</span><strong>{dossier.model_route}</strong></div>
        <div><span>Evidence Gate</span><strong>{evidenceLoading ? "lade" : evidenceGate?.status ?? "offen"}</strong></div>
        <div><span>Evidence Bundles</span><strong>{evidenceGate?.evidence_count ?? "—"}</strong></div>
      </div>

      <div className="run-focus-actions">
        <button
          type="button"
          className="btn small"
          onClick={onReplay}
          disabled={replayPending}
        >
          {replayPending ? "Replay laeuft" : replay ? "Replay erneut prüfen" : "Deterministisch replayen"}
        </button>
        <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(dossier.run_id)}`}>Run öffnen</Link>
        {replay && (
          <Link
            className="btn ghost small"
            to={`/app/agents?run=${encodeURIComponent(replay.run_id)}&replay=${encodeURIComponent(replay.replay_id)}`}
          >
            Replay öffnen
          </Link>
        )}
        {dossier.evidence_id && (
          <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(dossier.evidence_id)}`}>Dossier Evidence</Link>
        )}
        {replay?.evidence_id && (
          <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(replay.evidence_id)}`}>Replay Evidence</Link>
        )}
      </div>

      {replay && (
        <div className={`replay-detail-card ${replay.status}`}>
          <div className="replay-detail-grid">
            <div><span>Replay</span><strong>{replay.replay_id}</strong></div>
            <div><span>Expected SHA</span><strong>{shortHash(replay.expected_sha256)}</strong></div>
            <div><span>Actual SHA</span><strong>{shortHash(replay.actual_sha256)}</strong></div>
            <div><span>Source Refs</span><strong>{replay.source_refs?.length ?? 0}</strong></div>
          </div>
        </div>
      )}

      {evidenceGate && (
        <ul className="lineage-list">
          {(evidenceGate.items ?? []).map((item) => (
            <li key={item.evidence_id}>
              <span className={`pill ${item.status === "pass" ? "good" : "warn"}`}>{item.status}</span>
              <code>{item.kind} · {item.evidence_id}</code>
              <span className="muted small">{shortHash(item.manifest_sha256)}</span>
              <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>Evidence öffnen</Link>
            </li>
          ))}
        </ul>
      )}

      {Boolean(replayError) && <p className="error">FRIA-Replay fehlgeschlagen: {errorMessage(replayError)}</p>}
      {Boolean(evidenceError) && <p className="error">Evidence-Gate konnte nicht geladen werden: {errorMessage(evidenceError)}</p>}
    </div>
  );
}

function shortHash(value?: string | null): string {
  return value ? value.slice(0, 12) : "—";
}

function errorMessage(error: unknown): string {
  return error && typeof error === "object" && "message" in error
    ? String((error as { message?: unknown }).message ?? "unbekannter Fehler")
    : "unbekannter Fehler";
}

function resealSummary(result: AuditResealResult | null): string {
  if (!result) return "";
  if ((result.kinds?.length ?? 0) === 0) return "Keine reparaturbeduerftigen Audit-Ketten.";
  const resealable = (result.kinds ?? []).filter((item) => item.resealable).length;
  return `${resealable}/${result.kinds?.length ?? 0} Ketten resealable`;
}

function formatRecordTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString("de-DE");
}

function resealRecordItem(record: AuditRecord): ResealAuditItem {
  return record.item as ResealAuditItem;
}

function resealRecordStatus(item: ResealAuditItem, record: AuditRecord): string {
  if (item.dry_run) return "dry-run";
  if (item.applied) return "applied";
  return record.action;
}

function resealRecordTotals(item: ResealAuditItem): { kindCount: number; recordCount: number } {
  const kinds = item.kinds ?? [];
  return {
    kindCount: kinds.length,
    recordCount: kinds.reduce((sum, kind) => sum + (kind.records ?? 0), 0),
  };
}

function AuditResealOperator({
  report,
  loading,
  error,
  dryRunResult,
  applyResult,
  dryRunPending,
  applyPending,
  dryRunError,
  applyError,
  records,
  recordsLoading,
  recordsError,
  onDryRun,
  onApply,
}: {
  report?: AuditIntegrityReport;
  loading: boolean;
  error: unknown;
  dryRunResult: AuditResealResult | null;
  applyResult: AuditResealResult | null;
  dryRunPending: boolean;
  applyPending: boolean;
  dryRunError: unknown;
  applyError: unknown;
  records: AuditRecord[];
  recordsLoading: boolean;
  recordsError: unknown;
  onDryRun: () => void;
  onApply: () => void;
}) {
  const brokenChains = report?.chains?.filter((chain) => chain.enabled && !chain.verified) ?? [];
  const canApply = Boolean(
    dryRunResult?.kinds?.length &&
    dryRunResult.kinds.every((item) => item.resealable && !item.applied && !item.error),
  );
  const visibleChains = brokenChains.length > 0 ? brokenChains : report?.chains?.filter((chain) => chain.records > 0).slice(0, 4) ?? [];

  return (
    <section className="audit-reseal-operator">
      <div className="section-head compact">
        <div>
          <p className="hero-eyebrow">Audit Reseal Operator</p>
          <h2>Hashketten kontrolliert linearisieren.</h2>
        </div>
        <span className={`status-pill ${report?.verified ? "good" : report ? "danger" : "warning"}`}>
          {loading ? "lade" : report?.verified ? "verified" : report ? "repair needed" : "unknown"}
        </span>
      </div>

      <div className="reseal-summary-grid">
        <div>
          <span>Audit-Kinds</span>
          <strong>{report ? `${report.verified_kinds}/${report.checked_kinds}` : "—"}</strong>
        </div>
        <div>
          <span>Broken</span>
          <strong>{report?.broken_kinds ?? "—"}</strong>
        </div>
        <div>
          <span>Records</span>
          <strong>{report?.total_records.toLocaleString("de-DE") ?? "—"}</strong>
        </div>
        <div>
          <span>Tenant</span>
          <strong>{report?.tenant_id ?? "—"}</strong>
        </div>
      </div>

      {Boolean(error) && <p className="error">Audit-Integrity-Report konnte nicht geladen werden.</p>}

      {visibleChains.length > 0 && (
        <ul className="reseal-chain-list">
          {visibleChains.map((chain: AuditChainStatus) => (
            <li key={chain.kind} className={chain.verified ? "verified" : "broken"}>
              <div>
                <strong>{chain.kind}</strong>
                <span>{chain.records.toLocaleString("de-DE")} Records</span>
              </div>
              <p>
                {chain.verified
                  ? `last ${shortHash(chain.last_hash)}`
                  : `${chain.error ?? "broken"} ${chain.broken_at ?? ""}`.trim()}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="reseal-actions">
        <button type="button" className="btn ghost" onClick={onDryRun} disabled={dryRunPending || applyPending}>
          {dryRunPending ? "Dry-run laeuft" : "Dry-run prüfen"}
        </button>
        <button type="button" className="btn" onClick={onApply} disabled={!canApply || applyPending || dryRunPending}>
          {applyPending ? "Reseal laeuft" : "Reseal anwenden"}
        </button>
        <Link className="btn ghost" to="/app/agents">Audit-Records öffnen</Link>
      </div>

      {dryRunResult && (
        <ResealResultPanel title="Dry-run Ergebnis" result={dryRunResult} summary={resealSummary(dryRunResult)} />
      )}
      {applyResult && (
        <ResealResultPanel title="Applied Reseal" result={applyResult} summary={applyResult.applied ? "Reseal archiviert und auditiert." : "Kein Reseal angewendet."} />
      )}
      {Boolean(dryRunError) && <p className="error">Dry-run fehlgeschlagen: {errorMessage(dryRunError)}</p>}
      {Boolean(applyError) && <p className="error">Reseal fehlgeschlagen: {errorMessage(applyError)}</p>}

      <ResealHistory records={records} loading={recordsLoading} error={recordsError} />
    </section>
  );
}

function ResealHistory({ records, loading, error }: { records: AuditRecord[]; loading: boolean; error: unknown }) {
  return (
    <div className="reseal-history">
      <div className="section-head compact">
        <div>
          <h3>Letzte Reseals</h3>
          <p className="muted small">Auditierte Reparaturen bleiben tenant-scoped sichtbar.</p>
        </div>
        <Link className="btn ghost small" to="/app/agents?kind=audit_reseals">Alle öffnen</Link>
      </div>

      {loading && <p className="muted small">Reseal-Historie wird geladen.</p>}
      {Boolean(error) && <p className="error">Reseal-Historie konnte nicht geladen werden.</p>}
      {!loading && !error && records.length === 0 && (
        <p className="muted small">Noch keine Audit-Reseals für diesen Tenant.</p>
      )}

      {records.length > 0 && (
        <ul className="reseal-history-list">
          {records.map((record) => {
            const item = resealRecordItem(record);
            const totals = resealRecordTotals(item);
            const id = item.id ?? record.item_id ?? record.audit_id;
            return (
              <li key={record.audit_id}>
                <div className="reseal-history-main">
                  <div>
                    <strong>{id}</strong>
                    <span>{formatRecordTime(record.ts)} · {resealRecordStatus(item, record)}</span>
                  </div>
                  <code>{shortHash(record.hash)}</code>
                </div>
                <div className="reseal-history-meta">
                  <span>{totals.kindCount.toLocaleString("de-DE")} Ketten</span>
                  <span>{totals.recordCount.toLocaleString("de-DE")} Records</span>
                  {item.archive_uri && <code>{item.archive_uri}</code>}
                </div>
                <div className="reseal-links">
                  <Link className="btn ghost small" to={`/app/agents?kind=audit_reseals&record=${encodeURIComponent(id)}`}>Record</Link>
                  {item.run_id && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(item.run_id)}`}>Run</Link>}
                  {item.evidence_id && <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>Evidence</Link>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ResealResultPanel({ title, result, summary }: { title: string; result: AuditResealResult; summary: string }) {
  return (
    <div className={`reseal-result ${result.applied ? "applied" : "dry-run"}`}>
      <div className="section-head compact">
        <div>
          <h3>{title}</h3>
          <p className="muted small">{summary}</p>
        </div>
        <code>{result.id}</code>
      </div>
      {(result.kinds?.length ?? 0) > 0 && (
        <ul className="reseal-result-list">
          {(result.kinds ?? []).map((item) => (
            <li key={item.kind} className={item.resealable ? "resealable" : "blocked"}>
              <div>
                <strong>{item.kind}</strong>
                <span>{item.records.toLocaleString("de-DE")} Records · {item.before_error ?? "verified"}</span>
              </div>
              <div className="reseal-hashes">
                <code>{shortHash(item.original_sha256)}</code>
                <code>{shortHash(item.resealed_sha256)}</code>
              </div>
              {item.error && <p>{item.error}</p>}
            </li>
          ))}
        </ul>
      )}
      {result.applied && (
        <div className="reseal-links">
          {result.run_id && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(result.run_id)}`}>Run öffnen</Link>}
          {result.evidence_id && <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(result.evidence_id)}`}>Evidence öffnen</Link>}
          <Link className="btn ghost small" to="/app/agents">Reseals anzeigen</Link>
        </div>
      )}
    </div>
  );
}

function TenantIsolationGate({
  status,
  loading,
  error,
  denyReport,
  denyLoading,
  denyError,
  persistence,
  persistenceLoading,
  persistenceError,
  modelRouting,
  modelRoutingLoading,
  modelRoutingError,
  modelDecision,
  modelDecisionPending,
  modelDecisionError,
  onModelDecision,
  modelApprovalPending,
  modelApprovalError,
  onModelApproval,
}: {
  status?: TenantIsolationStatus;
  loading: boolean;
  error: unknown;
  denyReport?: TenantDenyReport;
  denyLoading: boolean;
  denyError: unknown;
  persistence?: TenantPersistenceReport;
  persistenceLoading: boolean;
  persistenceError: unknown;
  modelRouting?: TenantModelRoutingReport;
  modelRoutingLoading: boolean;
  modelRoutingError: unknown;
  modelDecision: ModelRoutingDecision | null;
  modelDecisionPending: boolean;
  modelDecisionError: unknown;
  onModelDecision: (payload: ModelRoutingDecisionRequest) => void;
  modelApprovalPending: boolean;
  modelApprovalError: unknown;
  onModelApproval: (decision: ModelRoutingDecision, action: "approve" | "reject") => void;
}) {
  const checks = status?.checks ?? [];
  const activeChecks = checks.filter((check) => check.release_blocking);
  const failures = checks.filter((check) => check.status === "fail").length;
  const roadmap = checks.filter((check) => check.status === "not_configured").length;
  const denyFixtures = denyReport?.fixtures ?? [];
  const persistenceCollections = (persistence?.collections ?? [])
    .filter((collection) => collection.records > 0 || ["agent_runs", "agent_replays", "evidence", "roi_simulations", "roi_sensitivity_reports"].includes(collection.kind))
    .slice(0, 6) ?? [];
  const visibleModelRuns = (modelRouting?.runs ?? [])
    .filter((run) => run.status !== "pass" || run.external_call_count > 0 || (run.missing_metadata?.length ?? 0) > 0)
    .slice(0, 6) ?? [];

  return (
    <section className="tenant-isolation-gate">
      <div className="section-head compact">
        <div>
          <p className="hero-eyebrow">Tenant Isolation Gate</p>
          <h2>Mandantentrennung als Release-Gate.</h2>
        </div>
        <span className={`status-pill ${status?.status === "fail" ? "danger" : status?.status === "warning" ? "warning" : "good"}`}>
          {loading ? "lade" : status?.status ?? "unbekannt"}
        </span>
      </div>

      <div className="isolation-summary">
        <div>
          <span>Tenant</span>
          <strong>{status?.tenant_id ?? "—"}</strong>
        </div>
        <div>
          <span>Release Checks</span>
          <strong>{activeChecks.length}</strong>
        </div>
        <div>
          <span>Fails</span>
          <strong>{failures}</strong>
        </div>
        <div>
          <span>Modellpolitik</span>
          <strong>{status?.external_model_policy ?? "—"}</strong>
        </div>
      </div>

      {Boolean(error) && <p className="error">Tenant-Isolation-Gate konnte nicht geladen werden.</p>}
      {Boolean(denyError) && <p className="error">Cross-Tenant-Deny-Fixtures konnten nicht geladen werden.</p>}
      {Boolean(persistenceError) && <p className="error">Persistence-Gate konnte nicht geladen werden.</p>}
      {Boolean(modelRoutingError) && <p className="error">Model-Routing-Gate konnte nicht geladen werden.</p>}
      {!loading && roadmap > 0 && (
        <p className="muted small">
          {roadmap} Produktionsspeicher-Gates sind transparent als Roadmap markiert; aktive Dev-Gates müssen pass bleiben.
        </p>
      )}
      {(modelRouting || modelRoutingLoading) && (
        <div className={`model-routing-panel ${modelRouting?.status ?? "loading"}`}>
          <div className="section-head compact">
            <div>
              <h3>Model Routing Policy</h3>
              <p className="muted small">
                {modelRouting ? `${modelRouting.external_model_policy} · ${modelRouting.audit_namespace}` : "Modellrouting wird geprüft"}
              </p>
            </div>
            <span className={`pill ${modelRouting?.status === "fail" ? "warn" : "good"}`}>
              {modelRoutingLoading ? "lade" : modelRouting?.status ?? "unbekannt"}
            </span>
          </div>
          <div className="model-routing-summary">
            <div><span>Runs</span><strong>{modelRouting?.run_count.toLocaleString("de-DE") ?? "—"}</strong></div>
            <div><span>External</span><strong>{modelRouting?.external_run_count.toLocaleString("de-DE") ?? "—"}</strong></div>
            <div><span>Violations</span><strong>{modelRouting?.violation_count.toLocaleString("de-DE") ?? "—"}</strong></div>
            <div><span>Warnings</span><strong>{modelRouting?.metadata_warning_count.toLocaleString("de-DE") ?? "—"}</strong></div>
          </div>
          <ul className="model-routing-list">
            {visibleModelRuns.map((run) => (
              <li key={run.run_id} className={`model-routing-item ${run.status}`}>
                <div>
                  <strong>{run.agent}</strong>
                  <span>{run.model_route ?? "no route"} · {run.external_call_count} external</span>
                </div>
                <p>{run.detail}</p>
                {(run.missing_metadata?.length ?? 0) > 0 && <code>missing {run.missing_metadata?.join(", ")}</code>}
                {run.external_call_count > 0 && (
                  <code>
                    decision {run.model_routing_decision_status}
                    {run.model_routing_decision_id ? ` · ${run.model_routing_decision_id}` : ""}
                  </code>
                )}
                {(run.evidence_refs?.length ?? 0) > 0 && <code>{run.evidence_refs?.[0]}</code>}
              </li>
            ))}
          </ul>
          <div className="model-routing-probes">
            <button
              type="button"
              className="btn ghost small"
              onClick={() => onModelDecision({
                agent: "semantic-mapper",
                model_route: "local:qwen3-enterprise-sensitive-v1",
                task: "semantic_mapping",
                data_classification: "confidential",
                contains_pii: false,
                anonymized: false,
              })}
              disabled={modelDecisionPending}
            >
              Local Qwen prüfen
            </button>
            <button
              type="button"
              className="btn ghost small"
              onClick={() => onModelDecision({
                agent: "benchmark-agent",
                model_route: "openrouter/deepseek-v4-flash",
                task: "public_benchmark",
                data_classification: "public",
                contains_pii: false,
                anonymized: true,
              })}
              disabled={modelDecisionPending}
            >
              External anonymisiert
            </button>
            <button
              type="button"
              className="btn ghost small"
              onClick={() => onModelDecision({
                agent: "c-level-briefing",
                model_route: "openrouter/deepseek-v4-flash",
                task: "executive_briefing",
                data_classification: "confidential",
                contains_pii: true,
                anonymized: false,
              })}
              disabled={modelDecisionPending}
            >
              External Rohdaten
            </button>
          </div>
          {modelDecision && (
            <div className={`model-routing-decision ${modelDecision.decision}`}>
              <div>
                <strong>{modelDecision.decision}</strong>
                <span>{modelDecision.requested_route} -&gt; {modelDecision.recommended_route}</span>
              </div>
              <p>{(modelDecision.reasons ?? []).join(" ")}</p>
              <div className="model-routing-approval-state">
                <span>Approval</span>
                <strong>{modelDecision.approval_status}</strong>
                {(modelDecision.approved_roles?.length ?? 0) > 0 && (
                  <code>approved {modelDecision.approved_roles?.join(", ")}</code>
                )}
              </div>
              {(modelDecision.required_approvals?.length ?? 0) > 0 && (
                <code>required {modelDecision.required_approvals?.join(", ")}</code>
              )}
              <code>{modelDecision.evidence_refs?.[0]}</code>
              {modelDecision.approval_status === "pending" && (
                <div className="model-routing-approval-actions">
                  <button
                    type="button"
                    className="btn small"
                    onClick={() => onModelApproval(modelDecision, "approve")}
                    disabled={modelApprovalPending}
                  >
                    Freigeben
                  </button>
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => onModelApproval(modelDecision, "reject")}
                    disabled={modelApprovalPending}
                  >
                    Ablehnen
                  </button>
                </div>
              )}
            </div>
          )}
          {Boolean(modelDecisionError) && (
            <p className="error">Model-Routing-Entscheid fehlgeschlagen: {errorMessage(modelDecisionError)}</p>
          )}
          {Boolean(modelApprovalError) && (
            <p className="error">Model-Routing-Freigabe fehlgeschlagen: {errorMessage(modelApprovalError)}</p>
          )}
        </div>
      )}
      {(denyReport || denyLoading) && (
        <div className={`deny-fixture-panel ${denyReport?.status ?? "loading"}`}>
          <div className="section-head compact">
            <div>
              <h3>Cross-Tenant Deny Fixtures</h3>
              <p className="muted small">{denyReport ? `${denyReport.fixture_count} Probes · ${denyReport.audit_namespace}` : "Probes laufen"}</p>
            </div>
            <span className={`pill ${denyReport?.status === "fail" ? "warn" : "good"}`}>
              {denyLoading ? "lade" : denyReport?.status ?? "unbekannt"}
            </span>
          </div>
          <div className="deny-fixture-summary">
            <div><span>Denied</span><strong>{denyReport?.passed_count ?? "—"}</strong></div>
            <div><span>Leaks</span><strong>{denyReport?.failed_count ?? "—"}</strong></div>
            <div><span>Roadmap</span><strong>{denyReport?.not_configured_count ?? "—"}</strong></div>
          </div>
          <ul className="deny-fixture-list">
            {denyFixtures.map((fixture) => (
              <li key={fixture.id} className={`deny-fixture ${fixture.status}`}>
                <span className={`pill ${fixture.status === "pass" ? "good" : fixture.status === "fail" ? "warn" : "muted"}`}>
                  {fixture.observed}
                </span>
                <div>
                  <strong>{fixture.fixture_kind}</strong>
                  <p>{fixture.detail}</p>
                  {(fixture.evidence_refs?.length ?? 0) > 0 && <code>{fixture.evidence_refs?.[0]}</code>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(persistence || persistenceLoading) && (
        <div className={`persistence-panel ${persistence?.status ?? "loading"}`}>
          <div className="section-head compact">
            <div>
              <h3>Audit Persistence &amp; Hydration</h3>
              <p className="muted small">
                {persistence ? `${persistence.backend} · ${persistence.audit_namespace}` : "Audit-Persistenz wird geprüft"}
              </p>
            </div>
            <span className={`pill ${persistence?.status === "fail" ? "warn" : "good"}`}>
              {persistenceLoading ? "lade" : persistence?.status ?? "unbekannt"}
            </span>
          </div>
          <div className="persistence-summary">
            <div><span>Audited</span><strong>{persistence?.audited_records.toLocaleString("de-DE") ?? "—"}</strong></div>
            <div><span>Hydrateable</span><strong>{persistence?.hydrateable_records.toLocaleString("de-DE") ?? "—"}</strong></div>
            <div><span>Verified</span><strong>{persistence?.verified_kinds.toLocaleString("de-DE") ?? "—"}</strong></div>
            <div><span>Broken</span><strong>{persistence?.broken_kinds.toLocaleString("de-DE") ?? "—"}</strong></div>
          </div>
          <ul className="persistence-list">
            {persistenceCollections.map((collection) => (
              <li key={collection.kind} className={`persistence-item ${collection.status}`}>
                <div>
                  <strong>{collection.kind}</strong>
                  <span>{collection.hydrateable_records.toLocaleString("de-DE")}/{collection.records.toLocaleString("de-DE")} hydrateable</span>
                </div>
                <p>{collection.detail}</p>
                {(collection.evidence_refs?.length ?? 0) > 0 && <code>{collection.evidence_refs?.[0]}</code>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="isolation-check-list">
        {checks.map((check) => (
          <li key={check.id} className={`isolation-check ${check.status}`}>
            <div>
              <strong>{check.title}</strong>
              <span>{check.layer} · {ISOLATION_LABEL[check.status] ?? check.status}</span>
            </div>
            <p>{check.detail}</p>
            {(check.evidence_refs?.length ?? 0) > 0 && (
              <code>{check.evidence_refs?.[0]}</code>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
